/**
 * Shared display helpers for patient search-result cards and rows. The Request Detail, Search
 * Patient Records, and Patient Access results screens render the same record shape — keep the
 * formatting here so they can't drift.
 */

import { PatientPortalUserStatusType } from '../../../core/models/patient-portal-user-status-type';
import { formatPhoneNumber } from '../date-time/date-time.utils';

/**
 * Shared "the record has none" text. Exported so the detail screens can label an
 * absent value the same way the cards do — a blank PHI field reads as a
 * rendering glitch, which staff can't tell apart from "nothing was submitted".
 */
export const NOT_AVAILABLE = 'Not available';

/**
 * The address fields these helpers format. Declared structurally rather than importing a feature's
 * model — `shared/` must not depend on feature code, and any record with these fields (such as
 * `RequestDetailAddress`) satisfies it without conversion.
 */
export interface PatientDisplayAddress {
  address1: string;
  address2: string | null;
  city: string | null;
  stateAbbreviation: string | null;
  postalCode: string | null;
}

/** First-and-last initials for the avatar chip, e.g. `Sarah M. Thompson` → `ST`. */
export function getPatientInitials(displayName: string | null | undefined): string {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * One-line address, or a fallback when the record has none.
 *
 * The `typeof` half of the guard is unreachable through the declared type and
 * is kept deliberately: this value is deserialized JSON from the EHR search,
 * so the type is a claim about the payload rather than a guarantee. A string
 * or number arriving here would otherwise destructure to `undefined`s and
 * render an empty address line on a PHI record.
 */
export function formatPatientAddress(address: PatientDisplayAddress | null | undefined): string {
  if (!address || typeof address !== 'object') {
    return NOT_AVAILABLE;
  }

  const { address1, address2, city, stateAbbreviation, postalCode } = address;
  const line1 = [address1, address2].filter(Boolean).join(' ');
  const locality = [city, stateAbbreviation, postalCode].filter(Boolean).join(' ');
  return [line1, locality].filter(Boolean).join(', ') || NOT_AVAILABLE;
}

/** Formatted phone number, or a fallback when the record has none. */
export function formatPatientPhone(phoneNumber: string | null | undefined): string {
  return formatPhoneNumber(phoneNumber, '') || NOT_AVAILABLE;
}

/** Medical record number, or a fallback when the record has none. */
export function formatPatientMrn(medicalRecordNumber: string | null | undefined): string {
  return medicalRecordNumber || NOT_AVAILABLE;
}

/** Email address, or a fallback when the record has none. */
export function formatEmailAddress(emailAddress: string | null | undefined): string {
  return emailAddress?.trim() || NOT_AVAILABLE;
}

const ACCOUNT_STATUS_LABELS: Record<PatientPortalUserStatusType, string> = {
  [PatientPortalUserStatusType.Active]: 'Active',
  [PatientPortalUserStatusType.Pending]: 'Pending',
  [PatientPortalUserStatusType.Revoked]: 'Revoked',
  [PatientPortalUserStatusType.Denied]: 'Denied',
};

/**
 * Patient-portal account status as staff-readable text, or the fallback when the
 * API sent no status (null) or one this build doesn't name.
 *
 * An unrecognized id falls back rather than rendering the bare number: a naked
 * "5" beside a patient's name reads as data, and staff would have no way to tell
 * it apart from a real status.
 */
export function formatPatientAccountStatus(statusTypeId: number | null | undefined): string {
  if (statusTypeId === null || statusTypeId === undefined) {
    return NOT_AVAILABLE;
  }
  return ACCOUNT_STATUS_LABELS[statusTypeId as PatientPortalUserStatusType] ?? NOT_AVAILABLE;
}

/**
 * Primary care provider, or a fallback when the record has none. Callers can
 * override the fallback text (the Confirm & Link modal's comparison column uses
 * a dash to match its other cells) — what must stay shared is the definition of
 * "the record has none", so an empty string can't render as a blank cell on one
 * screen and as absent on another.
 */
export function formatPatientPcp(
  primaryCareProvider: string | null | undefined,
  fallback = NOT_AVAILABLE,
): string {
  return primaryCareProvider || fallback;
}

/** Matches a lone initial ("M" or "M.") between the first and last names. */
const INITIAL_PATTERN = /^[A-Za-z]\.?$/;

/**
 * Split a display name into search-friendly first/last parts. Middle tokens
 * that look like initials ("Sarah M. Thompson") are dropped so they don't
 * leak into the last name and poison a patient search, but multi-word
 * surnames ("Maria De La Cruz") are kept intact.
 *
 * Nullable input, like the other helpers here: a request with no submitted name
 * yields empty parts, which prefills nothing rather than throwing.
 */
export function splitDisplayName(displayName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  const surnameParts = parts.slice(1).filter(
    (part, index, middle) =>
      // Always keep the final token; drop initials in between.
      index === middle.length - 1 || !INITIAL_PATTERN.test(part),
  );
  return {
    firstName: parts[0],
    lastName: surnameParts.join(' '),
  };
}
